import { Component, Inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';

export interface DenialReasonDialogData {
    mode: 'add' | 'edit';
    langs: string[];
    reason?: { [lang: string]: { title: string; internalDescription: string; citizenReason: string } };
}

@Component({
    selector: 'app-denial-reason-dialog',
    templateUrl: './denial-reason-dialog.component.html',
    styleUrls: ['./denial-reason-dialog.component.scss'],
})
export class DenialReasonDialogComponent {
    form: FormGroup;
    langs: string[];
    mode: 'add' | 'edit';

    private fieldLabels: { [lang: string]: { [key: string]: string } } = {};

    constructor(
        public dialogRef: MatDialogRef<DenialReasonDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: DenialReasonDialogData,
        private translate: TranslateService,
    ) {
        this.mode = data.mode;
        this.langs = data.langs;
        this.form = this.buildForm();
        this.loadFieldLabels();
    }

    private buildForm(): FormGroup {
        const group: { [key: string]: FormGroup } = {};
        for (const lang of this.langs) {
            const existing = this.data.reason?.[lang];
            group[lang] = new FormGroup({
                title: new FormControl(existing?.title || '', [Validators.required]),
                internalDescription: new FormControl(existing?.internalDescription || ''),
                citizenReason: new FormControl(existing?.citizenReason || '', [Validators.required]),
            });
        }
        return new FormGroup(group);
    }

    private loadFieldLabels(): void {
        const keys = [
            'titleLabel',
            'titlePlaceholder',
            'internalDescriptionLabel',
            'internalDescriptionPlaceholder',
            'citizenReasonLabel',
            'citizenReasonPlaceholder',
            'errors.titleRequired',
            'errors.citizenReasonRequired',
        ];
        for (const lang of this.langs) {
            const translations = this.translate.translations[lang];
            this.fieldLabels[lang] = {};
            for (const key of keys) {
                const fullKey = `pages.config-all.denial-reasons.dialog.${key}`;
                this.fieldLabels[lang][key] = this.resolveNestedKey(translations, fullKey) || '';
            }
        }
    }

    private resolveNestedKey(obj: any, key: string): string {
        return key.split('.').reduce((o, k) => (o?.[k] !== undefined ? o[k] : null), obj);
    }

    getFieldLabel(key: string, lang: string): string {
        return this.fieldLabels[lang]?.[key] || '';
    }

    getLangGroup(lang: string): FormGroup {
        return this.form.get(lang) as FormGroup;
    }

    submit(): void {
        if (this.form.valid) {
            this.dialogRef.close(this.form.value);
        } else {
            this.form.markAllAsTouched();
        }
    }

    cancel(): void {
        this.dialogRef.close(null);
    }
}
